import { type HttpClientError } from 'core/infra/http/HttpClient'

import { createAgnosticHttpClient } from 'auth/infra/createAgnosticHttpClient'

import { type DistanceUnit } from '../domain/DistanceUnit'
import { CapacityError } from '../domain/errors/CapacityError'
import { OutOfSyncError } from '../domain/errors/OutOfSyncError'
import { RatePerMileError } from '../domain/errors/RatePerMileError'
import { type LanePreference } from '../domain/LanePreferences'
import {
  getSubmitAction,
  type LaneProposal,
  ProposalAction,
  hasOutOfSyncError,
  hasErrors,
} from '../domain/LaneProposal'
import { ProposalRepository } from '../infra/ProposalRepository'
import { createLaneProposalFactory } from './createLaneProposal'
import { deleteLaneProposalFactory } from './deleteLaneProposal'
import { updateLaneProposalFactory } from './updateLaneProposal'

export type SubmitLaneProposalFactoryDependencies = {
  repository: ProposalRepository
}

export type SubmitLaneProposalParams = {
  currentLaneProposal: LaneProposal | undefined
  capacityValue: string | number
  flatRateValue: string | number
  ratePerDistanceValue: string | number
  laneId: number
  rfpId: string
  distanceUnit: DistanceUnit
  preference?: LanePreference
}

type LaneProposalErrorData = {
  capacity: string[]
  per_mile_rate: string[]
}

export function submitLaneProposalFactory({ repository }: SubmitLaneProposalFactoryDependencies) {
  const createLane = createLaneProposalFactory({ repository })
  const updateLane = updateLaneProposalFactory({ repository })
  const deleteLane = deleteLaneProposalFactory({ repository })

  return async function submitLaneProposalImpl(
    params: SubmitLaneProposalParams,
    config: AbortController
  ) {
    const {
      currentLaneProposal,
      rfpId,
      laneId,
      flatRateValue,
      ratePerDistanceValue,
      capacityValue,
      distanceUnit,
      preference,
    } = params

    const flatRate = Number(flatRateValue)
    const perDistanceRate = Number(ratePerDistanceValue)
    const capacity = Number(capacityValue)

    const action = getSubmitAction(currentLaneProposal, {
      newFlatRate: flatRate,
      newPerMileRate: perDistanceRate,
      newCapacity: capacity,
    })

    try {
      if (action === ProposalAction.Create) {
        return await createLane({
          createParams: {
            capacity,
            flatRate,
            laneId,
            perDistanceRate,
            rfpId,
            distanceUnit,
            preference,
          },
          config,
        })
      }

      if (action === ProposalAction.Update) {
        return updateLane({
          updateParams: {
            capacity,
            flatRate,
            laneId,
            laneProposalId: currentLaneProposal!.id,
            perDistanceRate,
            rfpId,
            distanceUnit,
            preference,
          },
          config,
        })
      }

      if (action === ProposalAction.Delete) {
        return deleteLane({
          deleteParams: {
            laneProposalId: currentLaneProposal!.id,
            rfpId,
          },
          config,
        })
      }
    } catch (error) {
      const errorData = (error as HttpClientError).response?.data

      const { hasCapacityError, hasRatePerMileError } = hasErrors(
        errorData as LaneProposalErrorData
      )

      if (hasOutOfSyncError(errorData)) {
        throw new OutOfSyncError()
      }

      if (hasCapacityError) {
        throw new CapacityError()
      }

      if (hasRatePerMileError) {
        throw new RatePerMileError()
      }

      throw new Error('Unable to update lane proposal', { cause: error as Error })
    }
  }
}

export function submitLaneProposal(params: SubmitLaneProposalParams, config: AbortController) {
  const httpClient = createAgnosticHttpClient()
  const repository = new ProposalRepository({ httpClient })
  const submitLane = submitLaneProposalFactory({ repository })
  return submitLane(params, config)
}
